import {
  type LBNodeData,
  type TimeOfDay,
  RepeatUnit,
} from '@/proto/logiblocks/logiblocks';
import { TRIGGER_ICON_COLOR } from '../../styles';
import {
  collectErrors,
  MSG_REQUIRED,
  validateRequiredArray,
} from '../../validation';

import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { ClockIcon } from '@/assets/icons/icons';
import { ScheduleTriggerV2Customization } from './ScheduleTriggerV2Customization';
import { defaultNodeData } from '../../graphUtils';
import {
  formatOrdinal,
  formatTimeOfDay,
  getDayOfWeekShortLabel,
} from '../../dateTimeUtils';

/** New schedules and newly added run times default to 9:00 AM. */
export const DEFAULT_SCHEDULE_TIME: TimeOfDay = { hour: 9, minute: 0 };

/** Validation field names for Schedule Trigger v2. Shared with the UI. */
export const ScheduleTriggerV2Fields = {
  REPEAT_INTERVAL: 'Repeat interval',
  REPEAT_EVERY: 'Repeat every',
  FREQUENCY: 'Frequency',
  DAYS_OF_WEEK: 'Days of week',
  DAYS_OF_MONTH: 'Days of month',
} as const;

const repeatEveryLabelMap: Record<RepeatUnit, string> = {
  [RepeatUnit.UNSPECIFIED]: '',
  [RepeatUnit.DAY]: 'day',
  [RepeatUnit.WEEK]: 'week',
  [RepeatUnit.MONTH]: 'month',
  [RepeatUnit.UNRECOGNIZED]: '',
};

export const ScheduleTriggerV2NodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'schedule_trigger_v2',

  category: NodeCategory.TRIGGER,

  label: 'Schedule Trigger',

  hasInput: false,

  getIcon: (size: number) => (
    <ClockIcon color={TRIGGER_ICON_COLOR} width={size} height={size} />
  ),

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    const schedule = data?.schedule_trigger_v2;
    if (
      !schedule?.repeat_interval ||
      schedule?.repeat_every === RepeatUnit.UNSPECIFIED
    ) {
      return undefined;
    }
    const { repeat_interval: interval, repeat_every: repeatEvery } = schedule;
    const intervalPrefix =
      interval === 1
        ? ''
        : `Every ${interval} ${repeatEveryLabelMap[repeatEvery]}s`;

    // Weekly and monthly schedules lead with the specific days they run on;
    // daily schedules and empty-day states fall back to the plain cadence.
    let lead: string;
    if (repeatEvery === RepeatUnit.WEEK && schedule.days_of_week.length > 0) {
      const dayNames = [...schedule.days_of_week]
        .sort((a, b) => a - b)
        .map(getDayOfWeekShortLabel)
        .join(', ');
      lead = intervalPrefix ? `${intervalPrefix} on ${dayNames}` : dayNames;
    } else if (
      repeatEvery === RepeatUnit.MONTH &&
      schedule.days_of_month.length > 0
    ) {
      const dayNums = [...schedule.days_of_month]
        .sort((a, b) => a - b)
        .map(formatOrdinal)
        .join(', ');
      lead = intervalPrefix
        ? `${intervalPrefix} (${dayNums})`
        : `Monthly (${dayNums})`;
    } else if (interval === 1) {
      if (repeatEvery === RepeatUnit.WEEK) {
        lead = 'Weekly';
      } else if (repeatEvery === RepeatUnit.MONTH) {
        lead = 'Monthly';
      } else {
        lead = 'Daily';
      }
    } else {
      lead = intervalPrefix;
    }

    const timePart = schedule.schedule_times.map(formatTimeOfDay).join(', ');
    return timePart ? `${lead} at ${timePart}` : lead;
  },

  getValidationErrors: (
    _nodeId: string,
    nodeData: LBNodeData,
    _graph: GraphData
  ) => {
    const schedule = nodeData?.schedule_trigger_v2;
    return collectErrors(
      schedule?.repeat_interval
        ? undefined
        : {
            type: 'REQUIRED_FIELD',
            field: ScheduleTriggerV2Fields.REPEAT_INTERVAL,
            message: MSG_REQUIRED,
          },
      schedule && schedule.repeat_every !== RepeatUnit.UNSPECIFIED
        ? undefined
        : {
            type: 'REQUIRED_FIELD',
            field: ScheduleTriggerV2Fields.REPEAT_EVERY,
            message: MSG_REQUIRED,
          },
      // Frequency is a UI-only control with no persisted field; choosing it is
      // what reveals the time pickers. The saved value is scheduleTimes, so we
      // validate that and report it under the Frequency field.
      validateRequiredArray(
        schedule?.schedule_times,
        ScheduleTriggerV2Fields.FREQUENCY
      ),
      // Weekly and monthly schedules must name the days they run on.
      schedule?.repeat_every === RepeatUnit.WEEK &&
        (schedule.days_of_week?.length ?? 0) === 0
        ? {
            type: 'REQUIRED_FIELD',
            field: ScheduleTriggerV2Fields.DAYS_OF_WEEK,
            message: MSG_REQUIRED,
          }
        : undefined,
      schedule?.repeat_every === RepeatUnit.MONTH &&
        (schedule.days_of_month?.length ?? 0) === 0
        ? {
            type: 'REQUIRED_FIELD',
            field: ScheduleTriggerV2Fields.DAYS_OF_MONTH,
            message: MSG_REQUIRED,
          }
        : undefined
    );
  },

  CustomizationComponent: ScheduleTriggerV2Customization,

  createDefaultNodeData: () =>
    defaultNodeData('schedule_trigger_v2', {
      repeat_interval: 1,
      repeat_every: RepeatUnit.DAY,
      days_of_week: [],
      days_of_month: [],
      schedule_times: [{ ...DEFAULT_SCHEDULE_TIME }],
      timezone: '',
    }),
};
